<script lang="ts" setup>
import { QItem, QItemSection } from 'quasar'
import { computed, ref, watch } from 'vue'
import { Color } from '@previo/codebooks'
import PPromotedBadge from '../badge/PPromotedBadge.vue'
import { PGlobalMenuItem, PGlobalMenuWithSubMenu } from './types'
import PList from './../list/PList.vue'
import PIcon from './../icon/PIcon.vue'
import PTextStandard from './../typography/body/PTextStandard.vue'
import PMenu from './../menu/PMenu.vue'
import PSpace from './../space/PSpace.vue'
import { PSpaceSize } from './../space/types'

const SUB_MENU_CLASS = 'ds-global-sub-menu'

type Props = {
  item: PGlobalMenuWithSubMenu | PGlobalMenuItem
}

type Emits = {
  (e: 'openSubMenu', value: boolean): void
  (e: 'closeMenu'): void
}

const props = withDefaults(defineProps<Props>(), {})

const emit = defineEmits<Emits>()

const isSubOpen = ref<boolean>(false)

const isWithoutSubMenu = computed<boolean>(() => {
  return props.item instanceof PGlobalMenuItem
})

const itemRoute = computed<null | string>(() => {
  return props.item instanceof PGlobalMenuItem ? props.item.route : null
})

const subItems = computed<(PGlobalMenuWithSubMenu | PGlobalMenuItem)[]>(() => {
  return props.item instanceof PGlobalMenuWithSubMenu ? props.item.items : []
})

const subItemRoute = (subItem: PGlobalMenuWithSubMenu | PGlobalMenuItem): null | string => {
  return subItem instanceof PGlobalMenuItem ? subItem.route : null
}

watch(isSubOpen, (val) => {
  emit('openSubMenu', val)
})

const onMouseEnter = (): void => {
  if (isWithoutSubMenu.value) {
    return
  }

  isSubOpen.value = true
}

const onMouseLeave = (event: MouseEvent): void => {
  if (isWithoutSubMenu.value) {
    return
  }

  // Keep open if cursor moved into the submenu. Safari sometimes drops
  // the mouseenter on the submenu, so check event.relatedTarget instead
  // of relying on the next mouseenter to fire.
  const nextTarget = event.relatedTarget as Element | null
  if (nextTarget?.closest(`.${SUB_MENU_CLASS}`)) {
    return
  }

  isSubOpen.value = false
}
</script>

<template>
  <component
    :is="isWithoutSubMenu ? 'router-link' : 'div'"
    v-if="item.isVisible"
    v-bind="isWithoutSubMenu ? { to: { name: itemRoute ?? undefined } } : {}"
    :data-cy="`menu-item-${item.key}`"
  >
    <q-item
      class="bg-primary"
      clickable
      @mouseenter="onMouseEnter"
      @mouseleave="onMouseLeave"
      @click="emit('closeMenu')"
    >
      <q-item-section v-if="item.icon" side>
        <p-icon :icon="item.icon" :color="Color.WHITE" />
      </q-item-section>

      <p-space v-if="!item.icon" :mobile="PSpaceSize.SM" />

      <q-item-section>
        <p-text-standard :text="item.title" :color="Color.WHITE" />
      </q-item-section>

      <q-item-section v-if="item.isPromoted" side>
        <p-promoted-badge />
      </q-item-section>

      <p-space :mobile="PSpaceSize.SM" />

      <template v-if="!isWithoutSubMenu">
        <q-item-section side>
          <p-icon icon="chevron-right" :color="Color.WHITE" />
        </q-item-section>

        <p-menu
          :model-value="isSubOpen"
          :class="SUB_MENU_CLASS"
          anchor="top end"
          self="top start"
          style="border-top-left-radius: 0; z-index: 2000"
          no-transition
          cy="global-navigation-sub-menu"
        >
          <p-list separator @mouseenter="onMouseEnter" @mouseleave="onMouseLeave">
            <template v-for="(subItem, n) in subItems" :key="n">
              <router-link
                v-if="subItem.isVisible"
                :to="{ name: subItemRoute(subItem) ?? undefined }"
              >
                <q-item clickable class="bg-primary">
                  <q-item-section v-if="subItem.icon" side>
                    <p-icon :icon="subItem.icon" :color="Color.WHITE" />
                  </q-item-section>

                  <p-space v-if="!subItem.icon" :mobile="PSpaceSize.SM" />

                  <q-item-section>
                    <p-text-standard :text="subItem.title" :color="Color.WHITE" />
                  </q-item-section>

                  <q-item-section v-if="subItem.isPromoted" side>
                    <p-promoted-badge />
                  </q-item-section>

                  <p-space :mobile="PSpaceSize.SM" />
                </q-item>
              </router-link>
            </template>
          </p-list>
        </p-menu>
      </template>
    </q-item>
  </component>
</template>
